import { useState } from "react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { WORK_TYPES } from "@/lib/constants";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Swords, Wind, Hand, Zap } from "lucide-react";

const WORK_META = {
  "Hard Sparring": { icon: Swords, desc: "Full-contact, fight-pace rounds." },
  "Technical/Flow Sparring": { icon: Wind, desc: "Controlled, light-contact skill work." },
  "Padwork/Clinch": { icon: Hand, desc: "Pads, clinch and positional drilling." },
};

export default function RequestModal({ fighter, open, onOpenChange }) {
  const queryClient = useQueryClient();
  const [workType, setWorkType] = useState("");
  const [message, setMessage] = useState("");
  const [scheduledAt, setScheduledAt] = useState("");
  const [loading, setLoading] = useState(false);

  const reset = () => { setWorkType(""); setMessage(""); setScheduledAt(""); };

  const submit = async () => {
    if (!workType) { toast.error("Pick the type of work you need."); return; }
    setLoading(true);
    try {
      await api.post("/requests", {
        to_fighter_id: fighter.id,
        work_type: workType,
        message,
        scheduled_at: scheduledAt || null,
      });
      toast.success(`Rounds requested with ${fighter.fighter_name}.`);
      queryClient.invalidateQueries({ queryKey: ["requests"] });
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!fighter) return null;

  return (
    <Dialog open={open} onOpenChange={(o) => { onOpenChange(o); if (!o) reset(); }}>
      <DialogContent className="bg-[#161616] border-[#27272a] text-white max-w-md" data-testid="request-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-3xl uppercase tracking-tight">
            Request Rounds
          </DialogTitle>
          <DialogDescription className="text-zinc-400">
            Sending a connection to <span className="text-volt font-medium">{fighter.fighter_name}</span> · {fighter.gym_name}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 mt-2">
          <p className="text-xs uppercase tracking-wider text-zinc-500">Type of work</p>
          {WORK_TYPES.map((w) => {
            const Meta = WORK_META[w];
            const active = workType === w;
            return (
              <button
                key={w}
                data-testid={`work-type-${w.split("/")[0].replace(/\s/g, "-").toLowerCase()}`}
                onClick={() => setWorkType(w)}
                className={`w-full flex items-center gap-3 p-3 rounded-sm border text-left transition-colors duration-200 ${
                  active ? "border-volt bg-volt/10" : "border-[#27272a] bg-[#0d0d0d] hover:border-volt/40"
                }`}
              >
                <Meta.icon size={20} className={active ? "text-volt" : "text-zinc-500"} />
                <div className="flex-1">
                  <div className="font-heading uppercase tracking-wide text-sm">{w}</div>
                  <div className="text-xs text-zinc-500">{Meta.desc}</div>
                </div>
                <div className={`w-4 h-4 rounded-full border-2 ${active ? "border-volt bg-volt" : "border-zinc-600"}`} />
              </button>
            );
          })}
        </div>

        <Textarea
          data-testid="request-message"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Add a note (optional) — availability, weight goals, ring vs mats…"
          className="bg-[#0d0d0d] border-[#27272a] text-white focus:ring-2 focus:ring-volt resize-none"
        />

        <div className="space-y-1.5">
          <label className="text-xs uppercase tracking-wider text-zinc-500 flex items-center gap-1.5"><Zap size={12} className="text-volt" /> Propose a time (optional)</label>
          <input
            data-testid="request-schedule"
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            className="w-full bg-[#0d0d0d] border border-[#27272a] text-white rounded-lg h-11 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-volt [color-scheme:dark]"
          />
        </div>

        <button
          data-testid="send-request-btn"
          onClick={submit}
          disabled={loading}
          className="w-full bg-volt text-black font-heading font-bold uppercase tracking-widest py-3 rounded-sm transition-colors duration-200 hover:bg-volt-hover disabled:opacity-40 flex items-center justify-center gap-2"
        >
          <Zap size={16} /> {loading ? "Sending…" : "Send Match Request"}
        </button>
      </DialogContent>
    </Dialog>
  );
}
